import { useState } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const honeypot = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypot && honeypot.value.trim() !== '') {
      setStatus('success');
      setEmail('');
      setTimeout(() => setStatus('idle'), 3000);
      return;
    }
    setStatus('loading');
    try {
      const formData = new URLSearchParams();
      formData.append('email', email);
      await fetch('https://readdy.ai/api/form/d8vrbcc6ci2plld1o8b0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });
      setStatus('success');
      setEmail('');
      setTimeout(() => setStatus('idle'), 3000);
    } catch {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 3000);
    }
  };

  const primeiraMetade = categories.slice(0, 7);
  const segundaMetade = categories.slice(7);

  return (
    <footer className="bg-primary-800 text-background-50">
      <div className="w-full px-4 md:px-6 py-14 md:py-16">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
          <div className="lg:w-2/5">
            <Link to="/" className="font-heading font-bold text-2xl text-background-50 whitespace-nowrap">
              CSSBuySheet<span className="text-accent-500">.</span>
            </Link>
            <p className="mt-4 text-sm text-foreground-300 leading-relaxed max-w-sm">
              O portal número um de CSSBuy Spreadsheet em português. Links verificados, produtos populares e guias completos para compras seguras.
            </p>
            <form onSubmit={handleSubmit} data-readdy-form="" className="mt-6 flex flex-col sm:flex-row gap-3 max-w-md">
              <input
                type="text"
                name="phone_alt"
                className="honeypot-field"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
              />
              <div className="flex-1 relative">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Seu e-mail"
                  required
                  className="w-full bg-primary-700 border border-primary-600 text-background-50 placeholder:text-foreground-400 rounded-full px-5 py-3 text-sm focus:outline-none focus:border-accent-500 transition-colors"
                />
              </div>
              <button
                type="submit"
                disabled={status === 'loading'}
                className="w-12 h-12 flex items-center justify-center bg-accent-500 text-primary-900 rounded-full hover:bg-accent-400 transition-colors cursor-pointer flex-shrink-0"
              >
                <i className={`text-lg ${status === 'loading' ? 'ri-loader-4-line animate-spin' : 'ri-mail-send-line'}`}></i>
              </button>
            </form>
            {status === 'success' && (
              <p className="mt-2 text-sm text-accent-400">Inscrição realizada com sucesso!</p>
            )}
            {status === 'error' && (
              <p className="mt-2 text-sm text-red-400">Erro ao inscrever. Tente novamente.</p>
            )}
            <p className="mt-3 text-xs text-foreground-400 italic">
              Receba links verificados diariamente
            </p>
          </div>

          <div className="lg:w-3/5 grid grid-cols-2 md:grid-cols-3 gap-8">
            <div>
              <h4 className="font-semibold text-sm text-background-50 mb-4 whitespace-nowrap">Produtos</h4>
              <ul className="space-y-2.5">
                {primeiraMetade.map((cat) => (
                  <li key={cat.name}>
                    <a
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="text-xs text-foreground-400 hover:text-accent-400 transition-colors whitespace-nowrap"
                    >
                      {cat.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-sm text-background-50 mb-4 whitespace-nowrap">Mais Produtos</h4>
              <ul className="space-y-2.5">
                {segundaMetade.map((cat) => (
                  <li key={cat.name}>
                    <a
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="text-xs text-foreground-400 hover:text-accent-400 transition-colors whitespace-nowrap"
                    >
                      {cat.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-sm text-background-50 mb-4 whitespace-nowrap">Guias</h4>
              <ul className="space-y-2.5">
                <li>
                  <Link to="/artigo/cssbuy-spreadsheet-guia-completo-2026" className="text-xs text-foreground-400 hover:text-accent-400 transition-colors whitespace-nowrap">
                    Guia Completo
                  </Link>
                </li>
                <li>
                  <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-xs text-foreground-400 hover:text-accent-400 transition-colors whitespace-nowrap">
                    CSSBuy Sheet
                  </a>
                </li>
                <li>
                  <span className="text-xs text-foreground-500 cursor-default">Segurança</span>
                </li>
                <li>
                  <span className="text-xs text-foreground-500 cursor-default">FAQ</span>
                </li>
                <li>
                  <span className="text-xs text-foreground-500 cursor-default">Sobre</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-primary-600 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-foreground-400">
            &copy; 2026 CSSBuySheet. Todos os direitos reservados.
          </p>
          <div className="flex items-center gap-4">
            <a href="#" className="w-9 h-9 flex items-center justify-center rounded-lg border border-primary-600 text-foreground-400 hover:text-accent-400 hover:border-accent-400 transition-colors cursor-pointer">
              <i className="ri-twitter-x-line text-sm"></i>
            </a>
            <a href="#" className="w-9 h-9 flex items-center justify-center rounded-lg border border-primary-600 text-foreground-400 hover:text-accent-400 hover:border-accent-400 transition-colors cursor-pointer">
              <i className="ri-instagram-line text-sm"></i>
            </a>
            <a href="#" className="w-9 h-9 flex items-center justify-center rounded-lg border border-primary-600 text-foreground-400 hover:text-accent-400 hover:border-accent-400 transition-colors cursor-pointer">
              <i className="ri-discord-line text-sm"></i>
            </a>
            <a href="#" className="w-9 h-9 flex items-center justify-center rounded-lg border border-primary-600 text-foreground-400 hover:text-accent-400 hover:border-accent-400 transition-colors cursor-pointer">
              <i className="ri-telegram-line text-sm"></i>
            </a>
          </div>
          <p className="text-xs text-foreground-500 whitespace-nowrap">
            Política de Privacidade
          </p>
        </div>
      </div>
    </footer>
  );
}